<template>
  <Flex
    justify="between"
    align="center"
    v-test="'sale-item'"
  >
    <Flex
      wrap
      align="center"
      :gap="0.5"
    >
      <div :class="$style.quantity">
        <BaseText>{{ item.quantity }}</BaseText>
      </div>
      <BaseText>{{ item.description }}</BaseText>
      <BaseText
        v-if="showDetails"
        color="secondary"
      >
        {{ details }}
      </BaseText>
      <BaseText
        v-if="item.usedPrepaidCard"
        color="secondary"
        size="s"
        v-test="'sale-item-usedprepaidcard'"
      >
        {{ `${$t('register.prepaid_card_applied', {
            card: item.usedPrepaidCard?.description
          })}`.toUpperCase() }}
      </BaseText>
    </Flex>
    <Flex :gap="0.5">
      <div v-if="hasDiscount">
        <BaseText
          lineThrough
          inline
          v-test="'sale-item-top-price-original'"
        >
          {{ filters.currency(item.quantity! * item.originalPrice!) }}
        </BaseText>
      </div>
      <BaseText v-test="'sale-item-top-price'">
        {{ filters.currency(item.price! * item.quantity!) }}
      </BaseText>
    </Flex>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import type { OrderItem } from '@/types';

const { t } = useI18n();

const props = defineProps<{ item: OrderItem }>();

const hasDiscount = computed(
  () =>
    props.item.originalPrice !== props.item.price &&
    !props.item.usedPrepaidCard,
);
const discount = computed(() => props.item.originalPrice! - props.item.price!);
const discountPercentage = computed(() =>
  props.item.originalPrice
    ? (discount.value / props.item.originalPrice) * 100
    : null,
);

const showDetails = computed(
  () =>
    hasDiscount.value ||
    (props.item.loyaltyPointsAmount && props.item.loyaltyPointsAmount > 0),
);

const details = computed(() => {
  const text = [];

  if (hasDiscount.value) {
    if (discount.value > 0) {
      if (discountPercentage.value !== null) {
        text.push(
          `${Math.round(discountPercentage.value)}% ${t('global.discount')}`,
        );
      } else {
        text.push(
          `${filters.currency(-discount.value)} ${t('global.discount')}`,
        );
      }
    } else {
      if (discountPercentage.value !== null) {
        text.push(
          `${Math.round(discountPercentage.value).toString().replace('-', '')}% ${t('dynamic_pricing.adjustment_types.percentage_increase')}`,
        );
      } else {
        text.push(
          `${filters.currency(-discount.value)} ${t('dynamic_pricing.adjustment_types.percentage_increase')}`,
        );
      }
    }
  }
  if (props.item.loyaltyPointsAmount && props.item.loyaltyPointsAmount > 0) {
    text.push(
      `${t('register.loyalty_points_used', { points: props.item.loyaltyPointsAmount })}`,
    );
  }

  return text.length > 0 ? `(${text.join(', ')})` : '';
});
</script>

<style
  lang="scss"
  module
>
.quantity {
  min-width: 20px;
}
</style>
